// Revisado: 2026-02-21 [I18n Scan] - [Meta SEO]
import React from 'react';
import { useTranslation } from 'react-i18next';
import SupportFilter from './SupportFilter';
import SupportTable from './SupportTable';
import SupportAnalytics from './SupportAnalytics';
import SupportsBests from './SupportsBests';
import SupportComparatorHeader from './SupportComparatorHeader';
import SupportCards from './DeckBuilder/SupportCards';
import SupportDeckBonus from './DeckBuilder/SupportDeckBonus';
import SupportListInventory from './DeckBuilder/SupportListInventory';
import DeckEventSummary from './DeckBuilder/DeckEventSummary';
import { API_BASE_URL } from '../../../constants';
import SEO from '../../../components/shared/SEO';
import { useAuth } from '../../../context/AuthContext';

const SupportComparator = () => {
    const { t } = useTranslation();
    const { isAuthenticated, token } = useAuth();

    const [data, setData] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);

    // Deck Builder State
    const [slots, setSlots] = React.useState(() => {
        try {
            const saved = localStorage.getItem('deckBuilder_draft');
            if (saved) {
                const parsed = JSON.parse(saved);
                // Basic validation: must be array of 6
                if (Array.isArray(parsed) && parsed.length >= 6) {
                    // Ensure strictly 6 elements just in case
                    return parsed.slice(0, 6);
                }
            }
        } catch (e) {
            console.warn("Failed to restore deck draft", e);
        }
        return Array(6).fill({ support: null, capLevel: 0 });
    });

    // Persist Draft
    React.useEffect(() => {
        localStorage.setItem('deckBuilder_draft', JSON.stringify(slots));
    }, [slots]);

    // Filters State
    const [selectedEffect, setSelectedEffect] = React.useState(''); // Mandatory
    const [selectedRarities, setSelectedRarities] = React.useState([]); // ['SSR', 'SR'] etc
    const [selectedTypes, setSelectedTypes] = React.useState([]); // ['Speed', 'Stamina']
    const [uniqueEnabled, setUniqueEnabled] = React.useState(false);
    const [selectedRegion, setSelectedRegion] = React.useState('jp'); // 'jp' (All) or 'global'

    // Inventory Integration
    const [onlyInventory, setOnlyInventory] = React.useState(false);
    const [inventory, setInventory] = React.useState({});
    const [inventoryLoading, setInventoryLoading] = React.useState(true);

    // Fetch Matrix
    React.useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/supports/matrix`);
                if (!response.ok) throw new Error('Failed to fetch matrix data');
                const result = await response.json();
                // console.log("Matrix Data:", result); // Debug removed DONT TOUCH
                setData(result);
            } catch (err) {
                console.error(err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // Fetch Inventory (Re-usable function)
    const fetchInventory = React.useCallback(async () => {
        if (!token) {
            setInventory({});
            setInventoryLoading(false);
            return;
        }
        try {
            const response = await fetch(`${API_BASE_URL}/api/user/inventory`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const res = await response.json();
                setInventory(res.data || {});
            }
        } catch (err) {
            console.error("Failed to load inventory for comparator:", err);
        } finally {
            setInventoryLoading(false);
        }
    }, [token]);

    // Initial Inventory Load
    React.useEffect(() => {
        fetchInventory();
    }, [fetchInventory]);


    // Helper: Extract Unique Effects List for Selector
    const effectsList = React.useMemo(() => {
        if (!data) return [];
        const allEffects = new Set();
        Object.values(data.matrix).forEach(card => {
            Object.keys(card.effects).forEach(eff => allEffects.add(eff));
            // Also add unique effects keys if we want to search by them? 
            // User requirement: "Filtro por effecto... algunos dicen Energy, hints..."
            // Usually we filter by "Normal Effect" keys.
        });
        return Array.from(allEffects).sort();
    }, [data]);

    // Filter and Calculate Data for Children (Table & Analytics)
    const filteredCards = React.useMemo(() => {
        if (!data || !selectedEffect) return [];

        const results = Object.entries(data.matrix).map(([id, card]) => {
            // 1. Check Filters Pre-calculation

            // Region Filter
            if (selectedRegion === 'global' && !card.info.release_en) {
                return null;
            }

            // Rarity Filter
            if (selectedRarities.length > 0 && !selectedRarities.includes(card.info.rarity)) {
                return null;
            }

            // Type Filter
            if (selectedTypes.length > 0 && !selectedTypes.includes(card.info.Support_Type)) {
                return null;
            }

            // Effect Check
            const exactMatch = card.effects[selectedEffect];
            let effectiveValues = exactMatch;

            if (exactMatch === undefined) {
                // Try case-insensitive / trimmed match as fallback
                const keys = Object.keys(card.effects);
                const normalizedSelected = (selectedEffect || '').toLowerCase().trim();
                const looseKey = keys.find(k => k.toLowerCase().trim() === normalizedSelected);

                if (looseKey) {
                    effectiveValues = card.effects[looseKey];
                } else {
                    return null; // Strict filter ON
                }
            }


            // --- INVENTORY FILTER LOGIC ---
            // Robust Lookup: Inventory might use Number(id) while matrix uses String(id)
            let userCap = 4; // Default: Max Cap (Legacy Mode)

            // ROBUST LOOKUP: Check both String(id) and Number(id)
            const invValNumber = inventory[Number(id)];
            const invValString = inventory[id];
            const invVal = (invValNumber !== undefined) ? invValNumber : invValString;

            if (onlyInventory) {
                if (invVal === undefined) return null; // Don't own it
                userCap = invVal; // 0, 1, 2, 3, or 4
            }
            // ------------------------------

            // 2. Calculate Values (Base + Unique)
            // Ensure inputs are treated as numbers to avoid string concatenation or NaN
            const normalValues = effectiveValues || [0, 0, 0, 0, 0];
            let uniqueBonusVal = 0;

            if (uniqueEnabled && card.unique_effects) {
                const match = card.unique_effects.find(u => u.description === selectedEffect);
                if (match) {
                    // Sanitize unique value parsing
                    const parsed = parseFloat(match.value);
                    uniqueBonusVal = isNaN(parsed) ? 0 : parsed;
                }
            }

            const values = normalValues.map((v, index) => {
                // If this cap index is higher than what the user owns, MASK IT out.
                // Only applies if `onlyInventory` is true.
                if (onlyInventory && index > userCap) {
                    return null; // VISUAL GAP
                }

                const cleanBase = parseFloat(v);
                const base = isNaN(cleanBase) ? 0 : cleanBase;
                return base + uniqueBonusVal;
            });

            return {
                ...card,
                id,
                values, // [v0, v1, v2, v3, v4] effective values (some might be null)
                uniqueBonusVal // passed for display breakdown
            };

        }).filter(c => c !== null);

        return results;
    }, [data, selectedEffect, selectedRarities, selectedTypes, uniqueEnabled, selectedRegion, onlyInventory, inventory]);

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white p-4 pt-32 md:p-8 md:pt-36">
            <SEO title={t('MetaSupportComparator.title')} description={t('MetaSupportComparator.description')} />
            <div className="max-w-7xl mx-auto space-y-8">

                {/* Header & Disclaimer */}
                <SupportComparatorHeader />

                {/* Global Bests Dashboard */}
                <SupportsBests
                    data={data}
                    inventory={inventory}
                    isGuest={!isAuthenticated}
                />

                {/* --- Deck Builder Section --- */}
                <div className="mb-8">
                    <SupportCards slots={slots} setSlots={setSlots} data={data} />
                    <SupportDeckBonus slots={slots} data={data} />
                    <DeckEventSummary slots={slots} />

                    {/* User Inventory Manager */}
                    <SupportListInventory
                        data={data}
                        onInventoryUpdate={fetchInventory} // Sync triggers re-fetch here
                    />
                </div>

                {/* Filter Section */}
                <SupportFilter
                    effects={effectsList}
                    selectedEffect={selectedEffect}
                    onEffectChange={setSelectedEffect}
                    selectedRarities={selectedRarities}
                    onRarityChange={setSelectedRarities}
                    selectedTypes={selectedTypes}
                    onTypeChange={setSelectedTypes}
                    uniqueEnabled={uniqueEnabled}
                    onUniqueToggle={() => setUniqueEnabled(!uniqueEnabled)}
                    selectedRegion={selectedRegion}
                    onRegionChange={setSelectedRegion}
                    loading={loading || inventoryLoading}

                    onlyInventory={onlyInventory}
                    onInventoryToggle={() => setOnlyInventory(!onlyInventory)}
                    isGuest={!isAuthenticated}
                />

                {/* Analytics Dashboard */}
                <SupportAnalytics cards={filteredCards} />

                {/* Table Section */}
                {error ? (
                    <div className="text-red-500 text-center p-4 bg-red-900/20 rounded-lg">
                        Error: {error}
                    </div>
                ) : (
                    <SupportTable
                        // Pass calculated cards directly instead of raw data + filters (mostly)
                        // But Table might need raw 'data' for nothing else if we pass filteredCards?
                        // SupportTable currently does its own filtering. We should update it to accept 'cards={filteredCards}' 
                        // and skip internal filtering.
                        filteredData={filteredCards}
                        selectedEffect={selectedEffect}
                        loading={loading || inventoryLoading}
                    />
                )}
            </div>
        </div>
    );
};

export default SupportComparator;
